import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SubscriptionTier } from '../../models/subscription/subscription.model';
import { MixpanelService } from '../mixpanel/mixpanel.service';

/**
 * SubscriptionService
 *
 * Manages user subscription tier and feature access.
 * Determines which features are available based on the user's subscription.
 *
 * TODO: Connect to backend API to get real subscription data
 */
@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private mixPanelService = inject(MixpanelService);

  /**
   * Current user's subscription tier
   * BehaviorSubject to allow components to subscribe to tier changes
   */
  private currentTier$ = new BehaviorSubject<SubscriptionTier>('free');

  /**
   * Observable of the current tier
   */
  public tier$: Observable<SubscriptionTier> = this.currentTier$.asObservable();

  constructor() {
    // TODO: Load user's actual subscription tier from backend
    this.loadUserTier();
  }

  /**
   * Loads the user's subscription tier
   * TODO: Replace with actual API call
   */
  private loadUserTier(): void {
    // Mock implementation - replace with actual backend call
    // Example:
    // this.http.get<{ tier: SubscriptionTier }>('/api/user/subscription')
    //   .subscribe(response => {
    //     this.currentTier$.next(response.tier);
    //   });

    // For now, default to 'free'
    this.currentTier$.next('free');
  }

  /**
   * Gets the current tier synchronously
   */
  getCurrentTier(): SubscriptionTier {
    return this.currentTier$.value;
  }

  /**
   * Checks if the user is a Game Master (has paid subscription)
   */
  isGameMaster(): boolean {
    return this.currentTier$.value === 'gamemaster';
  }

  /**
   * Checks if the user has access to a specific feature
   * @param featureId - The ID of the feature to check
   * @returns true if the user has access to the feature
   */
  hasFeatureAccess(featureId: string): boolean {
    const tier = this.getCurrentTier();

    // Free tier features
    const freeFeatures = ['play-games', 'create-locally', 'use-content-packs'];

    // Game Master tier features (includes all free features)
    const gameMasterFeatures = [
      ...freeFeatures,
      'invite-collaborators',
      'realtime-collaboration',
      'cloud-saves',
      'create-content-packs',
      'one-click-publishing',
      'shareable-link',
      'export-standalone',
    ];

    if (tier === 'gamemaster') {
      return gameMasterFeatures.includes(featureId);
    }

    return freeFeatures.includes(featureId);
  }

  /**
   * Attempts to access a premium feature
   * Returns true if the user has access, false if they need to upgrade
   * @param featureId - The ID of the feature being accessed
   * @param featureName - The human-readable name of the feature (for analytics)
   */
  tryAccessFeature(featureId: string, featureName?: string): boolean {
    const hasAccess = this.hasFeatureAccess(featureId);

    if (!hasAccess) {
      this.mixPanelService.trackEvent('premium_feature_blocked', {
        featureId,
        featureName: featureName || featureId,
        currentTier: this.getCurrentTier(),
      });
    }

    return hasAccess;
  }

  /**
   * Updates the user's subscription tier
   * TODO: This should be called after successful payment
   * @param tier - The new subscription tier
   */
  updateTier(tier: SubscriptionTier): void {
    const previousTier = this.currentTier$.value;
    this.currentTier$.next(tier);

    this.mixPanelService.trackEvent('subscription_tier_changed', {
      previousTier,
      newTier: tier,
    });
  }

  /**
   * Upgrades the user to Game Master tier
   * TODO: This should trigger the payment flow
   */
  upgradeToGameMaster(): void {
    this.mixPanelService.trackEvent('upgrade_to_gamemaster_initiated');

    // TODO: Implement payment flow here
    // For now, just log
    console.log('Upgrade to Game Master tier - Payment flow not yet implemented');

    // After successful payment:
    // this.updateTier('gamemaster');
  }

  /**
   * Cancels the user's subscription
   * TODO: Implement cancellation flow with backend
   */
  cancelSubscription(): void {
    this.mixPanelService.trackEvent('subscription_cancelled', {
      tier: this.getCurrentTier(),
    });

    // TODO: Call backend API to cancel subscription
    // After successful cancellation:
    // this.updateTier('free');
  }
}
